import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../../context/AuthContext';
import { skillService } from '../../../services/skill.service';
import AddSkillModal from './AddSkillModal';
import './SkillTracker.css';

const SkillTracker = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const { currentUser, authActions } = useAuth();

  useEffect(() => {
    if (currentUser) {
      loadUserSkills();
    }
  }, [currentUser]);

  const loadUserSkills = async () => {
    setLoading(true);
    try {
      const userSkills = await skillService.getUserSkills(currentUser.uid);
      setSkills(userSkills);
    } catch (error) {
      console.error('Error loading skills:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateProgress = (milestones) => {
    if (!milestones.length) return 0;
    const completedCount = milestones.filter(m => m.completed).length;
    return Math.round((completedCount / milestones.length) * 100);
  };

  const toggleMilestone = async (skillId, milestoneId) => {
    try {
      const skillToUpdate = skills.find(s => s.id === skillId);
      const updatedMilestones = skillToUpdate.milestones.map(milestone =>
        milestone.id === milestoneId 
          ? { ...milestone, completed: !milestone.completed }
          : milestone
      );

      // Update local state
      setSkills(prevSkills => prevSkills.map(skill => {
        if (skill.id === skillId) {
          return {
            ...skill,
            milestones: updatedMilestones,
            progress: calculateProgress(updatedMilestones)
          };
        }
        return skill;
      }));

      // Update in Firebase
      await skillService.updateSkill(skillId, {
        milestones: updatedMilestones,
        progress: calculateProgress(updatedMilestones)
      });
    } catch (error) {
      console.error('Error updating milestone:', error);
      // Revert local state on error
      loadUserSkills();
    }
  };

  const handleAddSkill = async (newSkill) => {
    try {
      const skillWithProgress = {
        ...newSkill,
        progress: calculateProgress(newSkill.milestones)
      };
      
      await skillService.createSkill(currentUser.uid, skillWithProgress);
      // Reload skills to get the new skill with Firebase ID
      await loadUserSkills();
    } catch (error) {
      console.error('Error adding skill:', error);
      // You might want to add error handling UI here
    }
  };

  const handleModalClose = () => {
    console.log('Closing modal');
    setIsModalOpen(false);
  };

  const handleAddClick = () => {
    setIsModalOpen(true);
  };

  const renderContent = () => {
    if (loading) {
      return <div className="loading-spinner">Loading skills...</div>;
    }

    if (skills.length === 0) {
      return (
        <div className="empty-state">
          <p>No skills added yet. Start tracking your progress by adding a skill!</p>
        </div>
      );
    }

    return (
      <div className="skills-list">
        {skills.map((skill, index) => (
          <motion.div 
            key={skill.id}
            className="skill-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <div className="skill-header">
              <h3>{skill.name}</h3>
              <div className="progress-wrapper">
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ width: `${calculateProgress(skill.milestones)}%` }}
                  />
                </div>
                <span className="progress-text">
                  {calculateProgress(skill.milestones)}%
                </span>
              </div>
            </div>

            <div className="milestones">
              <h4>Milestones</h4>
              <div className="milestone-list">
                {skill.milestones.map(milestone => (
                  <div 
                    key={milestone.id}
                    className="milestone-item"
                    onClick={() => toggleMilestone(skill.id, milestone.id)}
                  >
                    <input
                      type="checkbox"
                      checked={milestone.completed}
                      onChange={() => {}}
                    />
                    <span className={milestone.completed ? 'completed' : ''}>
                      {milestone.title}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="resources">
              <h4>Learning Resources</h4>
              <div className="resource-list">
                {skill.resources.map(resource => (
                  <a 
                    key={resource.id}
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="resource-link"
                  >
                    {resource.title}
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    );
  };

  return (
    <div className="section-container">
      <motion.div 
        className="skill-tracker-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="section-header">
          <h2>My Skills</h2>
          <button 
            className="primary-button"
            onClick={handleAddClick}
          >
            <span className="button-icon">+</span>
            Add Skill
          </button>
        </div>
        
        {renderContent()}
      </motion.div>

      <AddSkillModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onAdd={handleAddSkill}
      />
    </div>
  );
};

export default SkillTracker; 